import { z } from 'zod';
import { FilterModelSchema } from './consensus.validators';

/**
 * Request body for `POST /api/v1/consensus/exports`. The configurationId is
 * accepted from the client for transparency but the dispatcher re-derives it
 * from the template — see DispatchExportFlowService.dispatchConsensusExport.
 */
export const StartConsensusExportBodySchema = z.object({
  configurationId: z.string().uuid(),
  consensusTemplateId: z.string().uuid(),
  format: z.enum(['CSV', 'XLSX']),
  /**
   * Number of decimal places for value cells (0–30), mirroring the forecast
   * export's `decimalDigits`. Optional for rolling-deploy safety; the dispatcher
   * defaults it when the request omits it.
   */
  decimalDigits: z.number().int().min(0).max(30).optional(),
  /**
   * Hierarchy level to aggregate to, as the ordered dimension columns of that
   * level (e.g. `['Region','Material']`), taken from the hierarchy the user is
   * currently viewing. Omitted/empty ⇒ a normal leaf-level export. The dispatcher
   * validates every column is a server-derived groupable dimension of the template
   * before it is used (anti-injection); it need not be a prefix of the template's
   * SAVED hierarchy, since "Change hierarchy" can extend the viewed hierarchy.
   */
  aggregationColumns: z.array(z.string()).optional(),
  /**
   * Active UI filters to scope the export to, when the user opts in.
   * `rawFilters` are the applied dimension filters; `filterCombinationIds` are
   * saved quick-filter sets resolved server-side. Both omitted ⇒ unfiltered.
   */
  rawFilters: z
    .record(
      z.string(),
      z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])),
    )
    .optional(),
  filterCombinationIds: z.array(z.string().uuid()).optional(),
  /**
   * First month (`yyyy-MM`) of the planner's selected range when it reaches
   * before the forecast horizon. The export then carries an Ist column per
   * pre-horizon month, ahead of the horizon columns. Omitted ⇒ horizon only,
   * the pre-look-back behaviour. The dispatcher clamps it to the months the
   * forecast actually has data for, so a stale/tampered value cannot widen the
   * pipeline fetch.
   */
  historyStartMonth: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional(),
});

export type StartConsensusExportBody = z.infer<
  typeof StartConsensusExportBodySchema
>;

export const CONSENSUS_EXPORT_KIND = 'consensus';

export const ConsensusExportSeriesSchema = z.enum(['FORECAST', 'CONSENSUS']);

export type ConsensusExportSeries = z.infer<typeof ConsensusExportSeriesSchema>;

export const ConsensusExportParamsSchema =
  StartConsensusExportBodySchema.extend({
    series: z
      .array(ConsensusExportSeriesSchema)
      .min(1)
      .default(['FORECAST', 'CONSENSUS']),
    timeframeEndMonth: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .optional(),
    includeTotals: z.boolean().default(false),
    includeDistributionKeys: z.boolean().default(false),
    planningHierarchy: z.array(z.string()).optional(),
    valueFilterModel: FilterModelSchema.optional(),
  });

export type ConsensusExportParams = z.infer<typeof ConsensusExportParamsSchema>;
export type ConsensusExportParamsInput = z.input<
  typeof ConsensusExportParamsSchema
>;

/** Query params for `GET /api/v1/consensus/exports`. */
export const ListConsensusExportsQuerySchema = z.object({
  consensusTemplateId: z.string().uuid(),
});

export type ListConsensusExportsQuery = z.infer<
  typeof ListConsensusExportsQuerySchema
>;
